<template>
  <div class="text-left justify-start align-start">
    <div class="d-flex justify-space-between align-center">
      <span class="donation-flow__step-title">
        {{ t('donationGuide.certificateDetails.title') }}
      </span>
      <span class="step-indicator">{{ t('donationGuide.progress.mobileText', { current: 1, total: 3 }) }}</span>
    </div>
    <!-- <p class="donation-flow__step-description mt-4 mb-4">
      {{ t('donationGuide.certificateDetails.description') }}
    </p> -->

    <v-form ref="form" v-model="valid" data-cy="certificate-details-form" lazy-validation>
      <!-- Donation cause -->
      <option-label v-if="showCause" class="mb-4 mt-8" :title="t('donationGuide.certificateDetails.donationRecipient')" font-size="16px" font-weight="800" />
      <v-card-text class="pa-0 mt-2">
        <div v-if="isMemoryGift" ref="causeSectionRef">
          <!-- Forced single charity: org group has exactly one cause, no option to change -->
          <div v-if="isForcedSingleCause && donationDetails.donationCause" class="donation-cause-display mb-8">
            <div class="donation-cause-content d-flex align-center flex-grow-1">
              <v-avatar size="40" class="mr-3">
                <img
                  v-if="donationDetails.donationCause.logoUrl"
                  :src="donationDetails.donationCause.logoUrl"
                  :alt="donationDetails.donationCause.name"
                  class="avatar-image"
                />
                <v-icon v-else size="40">mdi-charity</v-icon>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="font-weight-bold">{{ donationDetails.donationCause.name }}</div>
              </div>
            </div>
          </div>

          <!-- Normal selection states (when not forced to a single cause) -->
          <template v-else>
            <!-- Empty state: nothing selected -->
            <div v-if="!donationDetails.donationCause" class="donation-cause-display mb-2" @click="openCharityModal">
              <div class="donation-cause-content d-flex align-center flex-grow-1">
                <div class="font-weight-bold" style="color: #1F2A37">{{ t('donationGuide.certificateDetails.chooseYourCharity') }}</div>
              </div>
              <v-btn
                variant="text"
                class="text-uppercase change-btn text-primary"
                @click.stop="openCharityModal"
              >
                {{ t('donationGuide.certificateDetails.selectButton') }}
              </v-btn>
            </div>

            <!-- Single charity box: shows current selection, subtitle reflects family vs user pick -->
            <div v-else class="donation-cause-display mb-8" @click="openCharityModal">
              <div class="donation-cause-content d-flex align-center flex-grow-1">
                <v-avatar size="40" class="mr-3">
                  <img
                    v-if="donationDetails.donationCause.logoUrl"
                    :src="donationDetails.donationCause.logoUrl"
                    :alt="donationDetails.donationCause.name"
                    class="avatar-image"
                  />
                  <v-icon v-else size="40">mdi-charity</v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="font-weight-bold">{{ donationDetails.donationCause.name }}</div>
                  <div class="text-grey mt-2">
                    {{ donationDetails.isFamilySuggested
                      ? t('donationGuide.certificateDetails.chosenByFamily')
                      : t('donationGuide.certificateDetails.pickedByYou') }}
                  </div>
                </div>
              </div>
              <v-btn variant="text" class="text-uppercase change-btn" @click.stop="openCharityModal">{{ t('donationGuide.certificateDetails.changeButton') }}</v-btn>
            </div>
          </template>

          <!-- Shown when user tries to proceed without selecting a charity -->
          <div v-if="causeError" class="text-error mb-8 mt-0">{{ causeErrorMessage }}</div>

          <!-- Donation cause selection modal -->
          <v-dialog 
            :model-value="causeModal"
            @update:model-value="onCauseModalToggle"
            max-width="500px" 
            :fullscreen="isSmallScreen"
            :transition="isSmallScreen ? 'dialog-bottom-transition' : 'dialog-transition'"
          >
            <v-card class="charity-modal d-flex flex-column">
              <!-- Modal Header -->
              <v-card-title class="d-flex justify-space-between align-center pa-4">
                <span class="font-weight-bold">{{
                  t('donationGuide.certificateDetails.title')
                }}</span>
                <v-btn icon variant="text" @click="cancelCharitySelection">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>

              <!-- Fixed Search Section -->
              <v-card-text class="pa-4 mb-0">
                <!-- Recipient organization heading -->
                <p class="donation-flow__option-title mb-3 mt-3">{{ t('donationGuide.certificateDetails.recipientOrganizationHeading') }}</p>

                <!-- Selected charity display -->
                <donation-cause-search-component
                  :hide-details="true"
                  :placeholder="t('donationDetails.form.donationCauseId.placeholder')"
                  :rules="[]"
                  :label="''"
                  :selected-cause="donationDetailsDonationCauseAny"
                  @search="handleSearchResults"
                  @clear="handleClearSearch"
                  @clear-selected-cause="handleClearSelectedCause"
                />
              </v-card-text>

              <v-divider></v-divider>

              <!-- Scrollable Content -->
              <div class="charity-modal-content">
                <div v-if="causeModalLoading" class="d-flex justify-center align-center py-12">
                  <v-progress-circular indeterminate color="primary" size="40" />
                </div>

                <div v-show="!causeModalLoading">
                <v-card-text v-if="filteredDefaultCauses.length > 0 && (!searchQuery || isOrgGroupRestricted)" class="pa-4 pb-0">
                  <p class="donation-flow__option-title mb-3">{{ isOrgGroupRestricted ? t('donationDetails.form.donationCauseId.autoComplete.tenantCausesHeader') : t('donationGuide.certificateDetails.chosenByFamily') }}</p>
                  <div class="preselected-charities d-flex flex-column ga-2">
                    <div
                      v-for="cause in filteredDefaultCauses"
                      :key="cause.id"
                      class="preselected-charity-item d-flex align-center pa-3 rounded-lg"
                      :class="{ selected: pendingCause?.id === cause.id }"
                      @click="selectPreselectedCause(cause)"
                    >
                      <v-avatar size="32" class="mr-3">
                        <img
                          v-if="cause.logoUrl"
                          :src="cause.logoUrl"
                          :alt="cause.name"
                          class="avatar-image"
                        />
                        <v-icon v-else size="32">mdi-charity</v-icon>
                      </v-avatar>
                      <div class="flex-grow-1">
                        <div class="font-weight-medium">{{ cause.name }}</div>
                      </div>
                      <v-icon
                        v-if="pendingCause?.id === cause.id"
                        color="primary"
                        size="20"
                      >
                        mdi-check-circle
                      </v-icon>
                    </div>
                  </div>
                </v-card-text>
                <!-- No results message for org group local search -->
                <v-card-text v-else-if="isOrgGroupRestricted && searchQuery.length >= 2" class="pa-4 pb-0">
                  <p class="text-grey">{{ t('donationCause.noCausesFound') }}</p>
                </v-card-text>

                <!-- Divider line (only show if pre-selected charities exist and infinite scroll will follow) -->
                <v-divider v-if="filteredDefaultCauses.length > 0 && !isOrgGroupRestricted && !searchQuery" class="my-4"></v-divider>

                <v-card-text v-if="!isOrgGroupRestricted" class="pa-4 pt-0">
                  <!-- Heading when user is searching -->
                  <p v-if="searchQuery" class="donation-flow__option-title mb-3 mt-6">{{ t('donationGuide.certificateDetails.listOfOrganizations') }}</p>
                  
                  <!-- Charity list with infinite scroll (only for non-restricted flows) -->
                  <donation-cause-infinite-scroll-component
                    v-model="donationDetailsDonationCauseAny"
                    :search-query="searchQuery"
                    :search-results="searchResults"
                    @ready="causeModalLoading = false"
                  />
                </v-card-text>
                </div>
              </div>

              <!-- Fixed Modal Actions -->
              <div class="charity-modal-actions d-flex align-center ga-6">
                <v-btn variant="outlined" color="primary" @click="cancelCharitySelection">
                  {{ t('generic.labels.cancel') }}
                </v-btn>
                <v-btn color="primary" variant="flat" @click="saveCharitySelection">
                  {{ t('generic.labels.saveChanges') }}
                </v-btn>
              </div>
            </v-card>
          </v-dialog>
        </div>
        <div v-else-if="isContribution">
          <!-- TODO kunna välja organizationGroup om det inte redan är valt i requestSession -->
          <v-autocomplete
            v-if="showCause"
            v-model="donationDetails.donationCause"
            data-cy="basic-certificate-details-autocomplete"
            :label="t('donationDetails.form.donationCauseId.label') + '*'"
            :placeholder="t('donationDetails.form.donationCauseId.placeholder')"
            :hint="t('donationDetails.form.donationCauseId.hint')"
            :allow-overflow="false"
            :menu-props="{ closeOnContentClick: true }"
            :items="activeDonationCauses"
            item-text="name"
            return-object
            :rules="[rules.requiredField(t('donationDetails.form.donationCauseId.label'))]"
            required
          >
            <template #item="{ item }">
              <div class="v-list-item__content">
                <v-list-item-title>
                  {{ item.name }}
                  <span v-if="item.bookingAccount" class="font-weight-medium">
                    | {{ requestSession.organizationGroup.giro }}
                  </span>
                </v-list-item-title>
                <v-list-item-subtitle v-if="requestSession.organizationGroup">
                  {{ requestSession.organizationGroup.name }}
                </v-list-item-subtitle>
              </div>
            </template>
          </v-autocomplete>
        </div>
      </v-card-text>

      <!-- Customise gift card toggle (sits above the dashed container) -->
      <div class="customise-gift-card-header d-flex align-center justify-space-between ga-3 mt-8">
        <span>
          <span class="customise-gift-card-header__title mr-1">{{ t('donationGuide.certificateDetails.customiseGiftCard.label') }}</span>
          <span class="customise-gift-card-header__optional">{{ t('donationGuide.certificateDetails.customiseGiftCard.optional') }}</span>
        </span>
        <v-switch
          v-model="customiseGiftCard"
          color="primary"
          hide-details
          class="customise-gift-card-toggle skip-theme-toggle flex-grow-0 flex-shrink-0"
          data-cy="certificate-details-customise-gift-card-switch"
        />
      </div>

      <!-- Gift card dashed container -->
      <div class="gift-card-container mt-6 pa-5 rounded-lg">

      <!-- Customisable gift card content (hidden when the customise toggle is off) -->
      <template v-if="customiseGiftCard">
      <!-- Gift card info box -->
      <div class="gift-card-info">
        <div class="gift-card-info__heading d-flex align-center ga-2 mb-2">
          <v-icon class="gift-card-info__icon" size="18">mdi-email-open-outline</v-icon>
          <span class="gift-card-info__title">{{ t('donationGuide.certificateDetails.giftCardInfo.title') }}</span>
        </div>
        <p class="gift-card-info__text">{{ t('donationGuide.certificateDetails.giftCardInfo.text') }}</p>
      </div>

       <!-- Toggle for skipping theme -->
      <v-switch
        v-model="addTheme"
        :label="t('certificateDetails.skipTheme.label')"
        color="primary"
        class="mt-8 skip-theme-toggle"
        data-cy="certificate-details-skip-theme-switch"
      />

      <!-- Image preview area -->
      <div v-if="addTheme">
        <option-label class="mt-2" :title="t('CertificateDetails.imagePicker.label')"/>
        <div class="w-100 mt-4">
          <!-- Display uploaded image when present, otherwise selected image from API -->
          <img
            :src="uploadedImageUrl || selectedImage?.uncompressedUrl || selectedImage?.url || ''"
            :alt="t('certificateDetails.previewCertificateAlt')"
            class="certificate-image-placeholder"
            :style="{ aspectRatio: String(certificatePhotoAspect) }"
          />
        </div>
        <v-row class="w-100 mt-5 pb-2" no-gutters dense>
          <v-col cols="12" sm="6" class="pa-1">
            <v-btn
              id="imagePickerChoiceButton"
              :aria-label="t('aria.label.selectImage')"
              text
              block
              class="image-action-btn"
              :ripple="false"
              dense
              :disabled="!images || images.length === 0"
              data-cy="image-picker-btn-pick"
              @click="showImagePickerModal = true"
            >
              {{ t('components.imagePicker.pick') }}
            </v-btn>
          </v-col>

          <v-col cols="12" sm="6" class="pa-1">
            <v-btn
              id="imagePickerUploadButton"
              :aria-label="uploadedImageUrl ? t('aria.label.changeImage') : t('aria.label.uploadImage')"
              text
              block
              class="image-action-btn"
              :ripple="false"
              dense
              data-cy="image-picker-btn-upload"
              @click="showUploadImageModal = true"
            >
              {{ uploadedImageUrl ? t('components.imagePicker.changeImage') : t('components.imagePicker.upload') }}
            </v-btn>
          </v-col>

          <ImagePickerModalSimplified
            v-model="showImagePickerModal"
            :categories="nonEmptyCategories"
            :images="images"
            @imageSelected="onImageSelected"
          />
          <UploadImageModal
            v-model="showUploadImageModal"
            @imageUploaded="onImageUploaded"
          />
        </v-row>
      </div>

      <!-- Recipient Name -->
      <option-label
        class="mt-4 mb-0"
        :title="t('donationGuide.certificateDetails.recipientNameLabel')"
      />
      <v-text-field
        v-model="recipientName"
        class="pt-0 mb-0"
        :placeholder="t('donationGuide.certificateDetails.recipientName.hint')"
        :rules="[rules.requiredField(t('donationDetails.form.recipientName.label'))]"
        data-cy="certificate-details-form-recipientname-field"
        required
        clearable
        @focus="onRecipientNameFocus"
      />
      <a
        v-if="showRecipientNameReset"
        class="recipient-name-reset d-inline-flex align-center"
        href="#"
        data-cy="certificate-details-form-recipientname-reset"
        @click.prevent="resetRecipientName"
      >
        <v-icon size="18" class="mr-3">mdi-rotate-right</v-icon> 
        {{ t('donationGuide.certificateDetails.recipientNameReset', { name: defaultRecipientName }) }}
      </a>
      <p class="recipient-name-helper">{{ t('donationGuide.certificateDetails.recipientNameHelper') }}</p>

      <!-- Goodbye message -->
      <option-label
        class="mt-3 mb-0"
        :title="t('donationGuide.certificateDetails.greetingLabel')"
      />
      <v-textarea
        v-model="greetingValue"
        :placeholder="t('donationGuide.certificateDetails.greetingPlaceholder')"
        class="pt-0 certificate-details-greeting-message"
        data-cy="certificate-details-form-message-textarea"
        rows="6"
        auto-grow
        :rules="[tooManyRowsRule]"
        clearable
      />

      <!-- Need inspiration? link + greeting suggestions panel -->
      <div v-if="exampleMessages && exampleMessages.length > 0" class="greeting-suggestions-wrapper">
        <button
          type="button"
          class="need-inspiration-link"
          data-cy="certificate-details-need-inspiration-link"
          @click="showSuggestions = !showSuggestions"
        >
          <v-icon size="18" class="mr-1">mdi-creation</v-icon>
          <span class="inspiration-text">{{ t('donationGuide.certificateDetails.needInspiration') }}</span>
        </button>

        <v-expand-transition>
          <div v-if="showSuggestions" class="greeting-suggestions-panel mt-3">
            <div class="greeting-suggestions-header d-flex align-center justify-space-between px-4 py-3">
              <span class="greeting-suggestions-title">{{ t('donationGuide.certificateDetails.greetingSuggestions.title') }}</span>
              <v-checkbox
                v-model="appendInsteadOfReplace"
                :label="t('donationGuide.certificateDetails.greetingSuggestions.appendInsteadOfReplace')"
                class="append-checkbox flex-grow-0 flex-shrink-0"
                color="primary"
                density="compact"
                hide-details
                data-cy="certificate-details-append-checkbox"
              />
            </div>
            <v-divider />
            <div class="greeting-suggestions-list">
              <div
                v-for="(item, index) in exampleMessages"
                :key="index"
                class="greeting-suggestion-item"
                data-cy="certificate-details-suggestion-item"
                @click="selectMessage(item)"
              >
                {{ item }}
              </div>
            </div>
            <v-divider />
            <div class="greeting-suggestions-footer d-flex justify-end px-3 py-2">
              <v-btn
                variant="text"
                class="text-uppercase"
                data-cy="certificate-details-suggestions-close-btn"
                @click="showSuggestions = false"
              >
                {{ t('generic.labels.close') }}
              </v-btn>
            </div>
          </div>
        </v-expand-transition>
      </div>
      </template><!-- end customisable gift card content -->

      <!-- Sender information box (shown only when gift card customisation is off) -->
      <div v-if="!customiseGiftCard" class="sender-info">
        <div class="sender-info__heading d-flex align-center ga-2 mb-2">
          <v-icon class="sender-info__icon" size="18">mdi-information-outline</v-icon>
          <span class="sender-info__title">{{ t('donationGuide.certificateDetails.senderInfo.title') }}</span>
        </div>
        <p class="sender-info__text">{{ t('donationGuide.certificateDetails.senderInfo.text') }}</p>
      </div>

      <!-- Anonymous sender toggle -->
      <v-switch
        v-model="anonymousSender"
        class="mt-0 mb-0 skip-theme-toggle"
        :label="t('donationGuide.certificateDetails.anonymousSender.label')"
        :rules="senderOrAnonymousRules"
        color="primary"
        data-cy="certificate-details-form-anonymous-checkbox"
      />

      <!-- Sender names -->
      <option-label v-if="!anonymousSender" class="mt-0 mb-0" :title="t('donationGuide.certificateDetails.senders')" />
      <v-textarea
        v-if="!anonymousSender"
        v-model="donorNameValue"
        class="pt-0 sender-name-field"
        :placeholder="t('donationGuide.donationCertificate.senderName.placeholder')"
        persistent-placeholder
        :validate-on-blur="true"
        auto-grow
        :rules="[tooManyRowsRule]"
        data-cy="certificate-details-form-sendername-textarea"
        rows="1"
        clearable
        @focus="onSenderNameFocus"
      />

      <v-slide-x-transition mode="out-in" :class="'elevation-0 pa-0 mt-5'">
        <v-switch
          v-show="showCondolenceOptions"
          v-model="donationDetails.showCondolenceOnMemorialPage"
          :false-value="false"
          :true-value="true"
          :disabled="condolenceToggleDisabled"
          class="no-sender-name skip-theme-toggle"
          color="primary"
          data-cy="certificate-details-form-no-sender-name-check"
        >
          <template #label>
            <div class="d-flex flex-column">
              <span class="font-weight-bold text-grey-darken-3">
                {{ t('donationGuide.certificateDetails.showCondolenceOnMemorialPage.label') }}
              </span>
              <span class="font-weight-regular text-medium-emphasis">
                {{ t('donationGuide.certificateDetails.showCondolenceOnMemorialPage.hint') }}
              </span>
            </div>
          </template>
        </v-switch>
      </v-slide-x-transition>
      </div><!-- end gift-card-container -->
    </v-form>

    <certificate-preview
      v-if="canPreviewCertificate"
      v-model="showCertificatePreview"
      :image-id="donationDetails.skipTheme ? blankImageId : donationDetailsImageIdAny"
      :image-url="donationDetails.skipTheme ? '' : uploadedImageUrl"
      :image-key="donationDetails.skipTheme ? '' : uploadedImageKey"
      :subject-name="giftCardDeceasedName"
      :donation-cause-id="donationCauseIdAny"
      :message="getAttributeValueById(attributes.greeting)"
      :donor-names="anonymousSender ? '' : getAttributeValueById(attributes.donorName)"
      @close="showCertificatePreview = false"
      @edit="showCertificatePreview = false"
    />

    <v-alert
      v-if="tooManyAttributeRows"
      class="mt-6"
      type="error"
      variant="tonal"
      data-cy="certificate-details-too-many-rows"
    >
      <h5>{{ t('validation.tooManyRowsTitle') }}</h5>
      {{ t('validation.tooManyRows') }}
    </v-alert>

    <div class="certificate-details-actions">
      <donation-flow-buttons
        :show-previous="showPreviousButton"
        previous-id="certificateDetailsViewBackButton"
        next-id="certificateDetailsViewNextButton"
        previous-data-cy="certificatedetailsview-back-button"
        next-data-cy="certificatedetailsview-next-button"
        :next-disabled="tooManyAttributeRows"
        @previous-step="$emit('previousStep')"
        @next-step="next"
      >
        <template #prepend>
          <v-btn
            id="previewButton"
            :disabled="!canPreviewCertificate || tooManyAttributeRows"
            data-cy="preview-certificate-btn"
            color="primary"
            variant="text"
            small
            rounded
            :aria-label="t('aria.label.showPreview')"
            @click="showCertificatePreview = !showCertificatePreview"
          >
            {{ t('certificateDetails.previewCertificate') }}
          </v-btn>
        </template>
      </donation-flow-buttons>
      <p class="certificate-terms" data-cy="certificate-terms-confirm">
        <i18n-t keypath="certificateDetails.termsConfirm" tag="span">
          <template #terms>
            <button
              type="button"
              class="certificate-terms__link"
              data-cy="certificate-terms-link"
              @click="showTermsOfUse = true"
            >
              {{ t('certificateDetails.termsOfUse') }}
            </button>
          </template>
        </i18n-t>
      </p>
    </div>

    <v-dialog
      v-model="showTermsOfUse"
      data-cy="certificate-terms-dialog"
      aria-labelledby="certificate-terms-dialog-title"
      aria-modal="true"
      max-width="720"
      scrollable
    >
      <v-card flat>
        <v-card-title id="certificate-terms-dialog-title" class="d-flex align-center">
          <span class="certificate-terms-dialog__title">{{ t('certificateDetails.termsOfUseTitle') }}</span>
          <v-spacer />
          <v-btn
            variant="text"
            icon
            data-cy="certificate-terms-dialog-close"
            :aria-label="t('certificateDetails.termsDialogCloseLabel')"
            @click="showTermsOfUse = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="certificate-terms-dialog__body" data-cy="certificate-terms-dialog-body">
          <terms-of-use-content />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, nextTick } from 'vue';
import store from '@/store';
import { useLabel } from '@/composables/useLabel';
import { useValidationRules } from '@/composables/useValidationRules';
import DonationCauseAutocompleteComponent from '@/components/organizationgroup/donationcause/DonationCauseAutocompleteComponent.vue';
import DonationCauseInfiniteScrollComponent from '@/components/organizationgroup/donationcause/DonationCauseInfiniteScrollComponent.vue';
import DonationCauseSearchComponent from '@/components/organizationgroup/donationcause/DonationCauseSearchComponent.vue';
import { AttributeType } from '@/enums/attributeType';
import { Donation } from '@/interfaces/donation/donation';
import { RequestSession } from '@/interfaces/requestSession';
import { DonationCause } from '@/interfaces/organizationgroup/donationCause';
import { AttributeValue } from '@/interfaces/donation/attributeValue';
import { DonationImage } from '@/interfaces/donation/donationImage';
import { DonationImageCategory } from '@/interfaces/donation/donationImageCategory';
import { BASE_URL, deleteRequest, ENDPOINTS, guideErrorMessage, guideHttpStatus } from '@/api/mainApi';
import { useDonationCauses } from '@/composables/useDonationCauses';
import { useImageOperations } from '@/composables/useImageOperations';
import { stepV2 } from '@/enums/donationStepOrder';
import {
  cleanTextFromTrailingEmptyRows,
  certificateMessageFits,
  CERTIFICATE_SENDER_MAX_CHARS,
  CertificateLayoutContext,
} from '@/utils';
import { CERTIFICATE_PHOTO_ASPECT } from '@/utils/certificateImage';
import ImagePickerComponentCompact from '@/components/image/ImagePickerComponentCompact.vue';
import ImagePickerIOSComponentCompact from '@/components/image/ImagePickerIOSComponentCompact.vue';
import ImagePickerModalSimplified from '@/components/image/ImagePickerModalSimplified.vue';
import UploadImageModal from '@/components/image/UploadImageModal.vue';
import OptionLabel from '@/components/OptionLabel.vue';
import CertificatePreview from '@/components/global/CertificatePreviewComponent.vue';
import DonationFlowButtons from '@/components/DonationFlowButtons.vue';
import TermsOfUseContent from '@/components/global/TermsOfUseContent.vue';
import { scrollToFirstInvalid } from '@/utils/formHelpers';
import matomo from '@/services/matomoService';
import { DonationCase } from '@/interfaces/donation/donationcase/donationCase';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  components: {
    CertificatePreview,
    ImagePickerComponentCompact,
    ImagePickerIOSComponentCompact,
    ImagePickerModalSimplified,
    UploadImageModal,
    TermsOfUseContent,
    DonationCauseAutocompleteComponent,
    DonationCauseInfiniteScrollComponent,
    DonationCauseSearchComponent,
    OptionLabel,
    DonationFlowButtons,
  },
  props: {
    showPrev: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['previousStep', 'nextStep'],
  setup(props, { emit }) {
    const { t } = useLabel();
    const { tm } = useI18n();
    const { rules: baseRules } = useValidationRules(t);
    const { fetchImagesAndCategories } = useImageOperations();
    const rules = {
      requiredField: baseRules.value.requiredField,
    };

    const form = ref();
    const hasBeenPrefilled = ref(false);
    const showCertificatePreview = ref(false);
    const anonymousSender = ref(false);
    const customiseGiftCard = ref(true); // Controls whether the gift card customisation fields are shown
    const isChecked = ref(false);
    const showSuggestions = ref(false);
    const appendInsteadOfReplace = ref(true);
    const causeModal = ref(false);
    const showImagePickerModal = ref(false);
    const showUploadImageModal = ref(false);
    const showTermsOfUse = ref(false);
    const uploadedImageUrl = ref('');
    const uploadedImageKey = ref('');
    const valid = ref(true);
    const showCondolenceOptions = ref(false);
    const showCause = ref(false);
    const causeError = ref(false);
    const causeErrorMessage = computed(
      () => t('donationDetails.form.donationCauseId.required')
    );
    const causeSectionRef = ref<HTMLElement | null>(null);
    const selectedImageId = ref(0);
    const blankImageId = ref<number | null>(null);
    const images = ref<DonationImage[]>([]);
    const categories = ref<DonationImageCategory[]>([]);
    const loading = ref(false);
    const searchQuery = ref('');
    const searchResults = ref<DonationCause[]>([]);
    const defaultSelectableDonationCauses = ref<DonationCause[]>([]);
    // Draft state for the charity modal: nothing touches donationDetails until Save changes
    const pendingCause = ref<DonationCause | null>(null);
    const pendingIsFamilySuggested = ref(false);
    const isForcedSingleCause = ref(false); // True when the org group has exactly one cause (no user choice)
    const causeModalLoading = ref(false); // True while the cause list is doing its initial fetch
    const defaultSelectableDonationCausesAny = computed(
      () => defaultSelectableDonationCauses.value
    );
    // charity_selected: a charity was pre-selected for the visitor (funeral home / request session),
    // with no visitor interaction. Reported once per charity.
    let lastPreselectedReportedId: number | null = null;
    const trackCharityPreselected = (cause: DonationCause | null | undefined) => {
      if (!cause || cause.id === lastPreselectedReportedId) return;
      lastPreselectedReportedId = cause.id;
      matomo.trackEvent(
        'certificate',
        'charity_selected',
        'donations_certificate_charity_selected'
      );
    };

    const donationDetailsDonationCauseAny = computed({
      get(): DonationCause | null {
        return pendingCause.value;
      },
      set(val: DonationCause | null) {
        pendingCause.value = val;
        pendingIsFamilySuggested.value = false; // User selection overrides family suggestion
      },
    });
    const donationDetailsImageIdAny = computed(() => {
      return donationDetails.value.skipTheme
        ? null
        : donationDetails.value.donationImageId || selectedImageId.value || null;
    });
    // Toggle for adding a photo/theme to the gift card. Inverts the underlying
    // `skipTheme` flag so the switch reads positively and is ON by default.
    const addTheme = computed({
      get: () => !donationDetails.value.skipTheme,
      set: (val: boolean) => {
        donationDetails.value.skipTheme = !val;
      },
    });
    const donationCauseIdAny = computed(() =>
      donationDetails.value.donationCause
        ? donationDetails.value.donationCause.id
        : donation.value?.donationCause?.id ||
          donation.value?.donationCase?.defaultDonationCauseIds?.[0] ||
          null
    );
    const defaultImage = ref<DonationImage | null>(null);
    const textAreaValidation = ref({
      message: {
        maxRowSize: 130,
      },
      donorNames: {
        maxRowSize: 125,
      },
      maxTotalRowCount: 17,
      valid: true,
    });
    const exampleMessages = computed(() => {
      const messages = tm('exampleMessages');
      return Array(messages) ? messages : [];
    });
    const attributes = ref({
      subjectName: AttributeType.SUBJECT_NAME,
      greeting: AttributeType.GREETING,
      donorName: AttributeType.DONOR_NAME,
    });
    const donationDetails = ref<any>({
      showCondolenceOnMemorialPage: false,
      attributeValues: [
        {
          value: '',
          attributeId: AttributeType.SUBJECT_NAME,
        },
        {
          value: '',
          attributeId: AttributeType.GREETING,
        },
        {
          value: '',
          attributeId: AttributeType.DONOR_NAME,
        },
      ] as Array<AttributeValue>,
      donationCause: null,
      donationImageId: '',
      skipTheme: false, // Toggle for "Don't add a theme to my certificate"
      isFamilySuggested: false, // Track if current charity was pre-selected by family
      familySuggestedCause: null, // Store the original family-suggested charity
    });

    const donorNameValue = computed({
      get: () => getAttributeValueById(attributes.value.donorName) || '',
      set: (value: string) => setAttributeValueById(attributes.value.donorName, value, false),
    });
    const senderOrAnonymousRules = computed(() => [
      () => {
        const senderName = getAttributeValueById(attributes.value.donorName)?.trim();
        if (senderName || anonymousSender.value) {
          return true;
        }
        return t('certificateDetails.validation.requiredFieldOrAnonymous');
      },
    ]);
    const recipientName = computed({
      get(): string {
        return getAttributeValueById(attributes.value.subjectName) || '';
      },
      set(value: string) {
        setAttributeValueById(attributes.value.subjectName, value, false);
      },
    });
    const isIphone = computed((): boolean => {
      const ua = navigator.userAgent || '';
      const isIpad = /\biPad\b/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document); // iPadOS 13+ quirk
      return /\biPhone\b/.test(ua) && !isIpad;
    });
    const currentStep = computed(() => store.state.currentStep);
    const greetingValue = computed({
      get(): string {
        return getAttributeValueById(attributes.value.greeting) || '';
      },
      set(value: string): void {
        setAttributeValueById(attributes.value.greeting, value, false);
      },
    });
    const hasCondolenceContent = computed(
      () =>
        !!donorNameValue.value.trim() ||
        (customiseGiftCard.value && !!greetingValue.value.trim())
    );
    const condolenceToggleDisabled = computed(() => !hasCondolenceContent.value);
    const giftCardDeceasedName = computed((): string => {
      return (
        getAttributeValueById(attributes.value.subjectName)?.trim() ||
        defaultRecipientName.value ||
        ''
      );
    });
    const canPreviewCertificate = computed((): boolean => {
      const hasRecipientName = !!giftCardDeceasedName.value;
      const hasCharity = !!donationDetails.value.donationCause;
      return hasRecipientName && hasCharity;
      // Old image requirement commented out:
      // && donationDetails.value.donationImageId &&
      // donationDetails.value.donationImageId > 0
    });
    const nonEmptyCategories = computed(() => {
      if (images.value) {
        return categories.value.filter(
          (c) => images.value.filter((i) => i.donationImageCategory.id === c.id).length
        );
      }
      return categories.value;
    });
    const requestSession = computed(() => store.state.requestSession as RequestSession);
    const donation = computed(() => store.state.donation as Donation);
    const isMemoryGift = computed((): boolean => true);
    const isContribution = computed((): boolean => false); // todo - currently not supported

    // The original name prefilled from the donation case (e.g. "Name_Surname")
    const defaultRecipientName = computed((): string => {
      const fromDonationCase = donation.value?.donationCase?.caseInfo?.name?.trim();
      if (fromDonationCase) {
        return fromDonationCase;
      }
      const donationCases: Array<DonationCase> = requestSession.value?.donationCases || [];
      if (donationCases.length === 1) {
        return donationCases[0]?.caseInfo?.name?.trim() || '';
      }
      return '';
    });
    // Show the reset link whenever the field differs from the original (e.g. after clearing it)
    const showRecipientNameReset = computed((): boolean => {
      return (
        !!defaultRecipientName.value &&
        (recipientName.value || '').trim() !== defaultRecipientName.value
      );
    });
    const resetRecipientName = () => {
      recipientName.value = defaultRecipientName.value;
    };
    const tooManyAttributeRows = computed((): boolean => {
      return donationDetails.value && isMemoryGift.value && !hasValidAttributeRowCount();
    });

    const tooManyRowsRule = () => {
      return hasValidAttributeRowCount() || t('validation.tooManyRows');
    };

    const hasDefaultDonationCauses = computed((): boolean => {
      let hasDefaultIds = (donation.value?.donationCase?.defaultDonationCauseIds?.length ?? 0) > 0;
      if (!hasDefaultIds) {
        if (requestSession.value?.donationCases?.length !== 1) {
          return false;
        }
        hasDefaultIds =
          (requestSession.value?.donationCases?.[0]?.defaultDonationCauseIds?.length ?? 0) > 0;
      }

      return hasDefaultIds;
    });
    const activeRequestSessionOrganizationGroupCauses = computed((): DonationCause[] => {
      if (requestSession.value.organizationGroup) {
        return requestSession.value.organizationGroup.donationCauses.filter(
          (donationCause: DonationCause) => {
            return donationCause.active;
          }
        );
      }
      return [];
    });
    const hasAnyActiveRequestSessionOrganizationGroupCauses = computed((): boolean => {
      return (
        !!activeRequestSessionOrganizationGroupCauses.value &&
        activeRequestSessionOrganizationGroupCauses.value.length > 0
      );
    });
    const activeDonationCauses = computed(() => {
      return requestSession.value?.organizationGroup?.donationCauses?.filter(
        (donationCause: DonationCause) => donationCause.active
      ) ?? [];
    });
    const hasSelectableCausesToDisplay = computed((): boolean => {
      return showCause.value && defaultSelectableDonationCauses.value.length > 0;
    });
    const hasOrganizationGroupCausesToDisplay = computed((): boolean => {
      return showCause.value && !hasAnyActiveRequestSessionOrganizationGroupCauses.value;
    });
    const isExternalAgent = computed((): boolean => {
      return requestSession.value?.externalAgent != null;
    });
    const isOnCertificateDetailsPage = computed((): boolean => {
      return currentStep.value === stepV2.CERTIFICATE_DETAILS;
    });
    const showPreviousButton = computed((): boolean => {
      // Hide the previous button if user is an external agent on the certificate details page
      // (since it's the first page they see, they can't go back)
      if (isExternalAgent.value && isOnCertificateDetailsPage.value) {
        return false;
      }
      return props.showPrev;
    });
    const isSmallScreen = computed((): boolean => {
      return typeof window !== 'undefined' && window.innerWidth <= 450;
    });
    // True when the org group has a restricted set of causes (no global search/browse should be offered)
    const isOrgGroupRestricted = computed((): boolean => {
      return hasAnyActiveRequestSessionOrganizationGroupCauses.value;
    });
    // When org group restricted and user is searching, filter the displayed causes locally
    const filteredDefaultCauses = computed((): DonationCause[] => {
      if (isOrgGroupRestricted.value && searchQuery.value && searchQuery.value.length >= 2) {
        const query = searchQuery.value.toLowerCase();
        return defaultSelectableDonationCauses.value.filter(
          c => c.name?.toLowerCase().includes(query)
        );
      }
      return defaultSelectableDonationCauses.value;
    });

    const onPreselectedDonationCauseChanged = (cause: DonationCause | null) => {
      if (!cause) return;
      const orgGroupId = cause.organizationGroup?.id;
      if (orgGroupId) {
        fetchImages(orgGroupId);
      }
    };

    const onPreselectedDonationCauseCleared = () => {
      donationDetails.value.donationCause = null;
      // Remove the default-cause mode so user gets the full autocomplete
      defaultSelectableDonationCauses.value = [];
    };

    const {
      getDonationCauseById: getDonationCauseWithId,
      getDonationCausesByIds: getDonationCausesWithIds,
    } = useDonationCauses();

    const getDefaultDonationCauseIds = (): number[] => {
      let defaultIds: number[] | null | undefined =
        donation.value?.donationCase?.defaultDonationCauseIds;
      if (!defaultIds || defaultIds.length === 0) {
        if (requestSession.value?.donationCases?.length === 1) {
          defaultIds = requestSession.value?.donationCases[0]?.defaultDonationCauseIds;
        }
      }
      return defaultIds || [];
    };

    const prefillRecipientNameFromCase = () => {
      if (hasBeenPrefilled.value) {
        return;
      }
      const currentValue = (recipientName.value || '').trim();
      if (currentValue.length > 0) {
        return;
      }

      // we check the donation's donation case first
      const nameFromDonationDonationCase: string =
        donation.value?.donationCase?.caseInfo?.name?.trim() ?? '';
      if (nameFromDonationDonationCase && nameFromDonationDonationCase.length > 0) {
        recipientName.value = nameFromDonationDonationCase;
        hasBeenPrefilled.value = true;
        return;
      }

      // then we check the request session's donation cases. But only if there is exactly one case
      const donationCases: Array<DonationCase> = requestSession.value?.donationCases || [];
      if (donationCases.length !== 1) {
        return; // only prefill if exactly one case exists
      }
      const dc = donationCases[0];
      const name = dc?.caseInfo?.name?.trim();
      if (name && name.length > 0) {
        hasBeenPrefilled.value = true;
        recipientName.value = name;
      }
    };

    const getAttributeValueById = (id: number): string => {
      const attributeValue = donationDetails.value.attributeValues.find(
        (a) => a.attributeId === id
      );
      return attributeValue ? attributeValue.value : '';
    };

    const setAttributeValueById = (id: number, value, concat: boolean) => {
      const attributeValue = donationDetails.value.attributeValues.find(
        (a) => a.attributeId === id
      );

      if (attributeValue) {
        if (concat) {
          attributeValue.value += value;
        } else {
          attributeValue.value = value;
        }
      }
    };

    const onRecipientNameFocus = () => {
      matomo.trackEvent('donations_certificate_name_entered', 'certificate', 'donations_certificate_recipient_name_entered');
    };

    const onSenderNameFocus = () => {
      matomo.trackEvent(
        'donations_certificate_sender_name_entered',
        'certificate',
        'donations_certificate_sender_name_entered'
      );
    };

    const selectedImage = computed(() => {
      return images.value.find(img => img.id === selectedImageId.value);
    });
    const certificateLayoutContext = computed((): CertificateLayoutContext => {
      const session = requestSession.value;
      const currentDonation = donation.value;
      const intermediary = session?.intermediary || currentDonation?.donationCase?.intermediary || null;
      return {
        hasPhoto: !donationDetails.value.skipTheme && !!(uploadedImageUrl.value || selectedImage.value || donationDetails.value.donationImageId),
        deceasedName: recipientName.value || '',
        causeName: donationDetails.value.donationCause?.name || '',
        hasCauseLogo: Boolean(donationDetails.value.donationCause?.logoUrl),
        senderName: anonymousSender.value
          ? ''
          : getAttributeValueById(attributes.value.donorName) || '',
        hasIntermediaryLogo: Boolean(intermediary?.logoUrl?.trim() && intermediary?.name?.trim()),
      };
    });

    const discardUploadedImage = () => {
      if (!uploadedImageKey.value) {
        return;
      }
      deleteRequest(ENDPOINTS.CERTIFICATE_IMAGE, undefined, false).catch((err) => {
        window.log?.warn?.('Could not delete unused certificate image', err);
      });
    };

    const onImageSelected = (imageId: number) => {
      selectedImageId.value = imageId;
      discardUploadedImage();
      uploadedImageUrl.value = '';
      uploadedImageKey.value = '';
      matomo.trackEvent('donations_certificate_image_selected', 'certificate', 'donations_certificate_image_selected');
    };

    const onImageUploaded = (payload: { url: string; dataUrl: string; imageKey?: string }) => {
      uploadedImageUrl.value = payload.url || payload.dataUrl;
      uploadedImageKey.value = payload.imageKey || '';
      matomo.trackEvent('donations_certificate_image_uploaded', 'certificate', 'donations_certificate_image_uploaded');
    };

    const hasValidAttributeRowCount = () => {
      const message = getAttributeValueById(AttributeType.GREETING) || '';
      const donorNames = anonymousSender.value
        ? ''
        : getAttributeValueById(AttributeType.DONOR_NAME) || '';

      if (donorNames.length > CERTIFICATE_SENDER_MAX_CHARS) {
        return false;
      }

      return certificateMessageFits(message, certificateLayoutContext.value);
    };

    const selectMessage = (message) => {
      matomo.trackEvent(
        'donations_certificate_message_selected',
        'certificate',
        'donations_certificate_message_selected'
      );
      if (appendInsteadOfReplace.value) {
        // Append to existing text, separating with a newline when there is already content
        const existing = getAttributeValueById(AttributeType.GREETING) || '';
        const separator = existing.trim() ? '\n' : '';
        setAttributeValueById(AttributeType.GREETING, separator + message, true);
      } else {
        // Replace any previously written text
        setAttributeValueById(AttributeType.GREETING, message, false);
      }
      showSuggestions.value = false;
    };

    const fetchImages = async (organizationGroupId?) => {
      try {
        const result = await fetchImagesAndCategories(organizationGroupId);
        if (result.categories) {
          categories.value = result.categories;
        }
        if (result.images) {
          const categoryOrder = ['Candles', 'Flowers', 'Nature', 'Landscape'];
          const allMapped = result.images.map((image: DonationImage) => {
            const baseUrl = typeof BASE_URL !== 'undefined' && !image.url.startsWith('http') ? BASE_URL : '';
            return {
              ...image,
              url: `${baseUrl}${image.url}?compression=true`,
              uncompressedUrl: `${baseUrl}${image.url}`,
            };
          });

          // Use the 'No Theme' category image as the blank, falling back to the first visible image
          const noThemeImage = allMapped.find(img => img.donationImageCategory?.name === 'No Theme');
          images.value = allMapped
            .filter(img => categoryOrder.includes(img.donationImageCategory?.name))
            .sort((a, b) => categoryOrder.indexOf(a.donationImageCategory?.name) - categoryOrder.indexOf(b.donationImageCategory?.name));
          blankImageId.value = noThemeImage?.id ?? (images.value.length > 0 ? images.value[0].id : null);

          // Always default to the first image in sorted order — org group defaults are ignored
          const userHasSelectedImage: boolean =
            selectedImageId.value > 0 &&
            images.value.findIndex((i) => i.id === selectedImageId.value) >= 0;
          if (!userHasSelectedImage) {
            selectedImageId.value = images.value.length > 0 ? images.value[0].id : 0;
          }
        }
      } catch (error) {
        window.log?.error('[Guide] certificate.images — Failed to fetch certificate images', {
          message: guideErrorMessage(error),
          httpStatus: guideHttpStatus(error),
        });
        store.commit('setSnackbar', {
          active: true,
          color: 'error',
          text: 'Failed to fetch images',
        });
        return;
      }
      
      // Temporary fix for dummy images - TODO: Remove when backend integration is complete
      // selectedImageId.value = 1;
    };

    const calculateShowCause = () => {
      showCause.value = true;
      if (
        activeRequestSessionOrganizationGroupCauses.value &&
        activeRequestSessionOrganizationGroupCauses.value.length === 1
      ) {
        const singleCause = activeRequestSessionOrganizationGroupCauses.value[0];
        isForcedSingleCause.value = true;
        donationDetails.value.donationCause = singleCause;
        donationDetails.value.familySuggestedCause = singleCause;
        donationDetails.value.isFamilySuggested = true;
        trackCharityPreselected(singleCause);
      } else {
        isForcedSingleCause.value = false;
      }
    };

    const cleanAttributeTexts = () => {
      const greetingAttribute = cleanTextFromTrailingEmptyRows(
        getAttributeValueById(attributes.value.greeting)
      );
      setAttributeValueById(attributes.value.greeting, greetingAttribute, false);

      const namesAttribute = cleanTextFromTrailingEmptyRows(
        getAttributeValueById(attributes.value.donorName)
      );
      setAttributeValueById(attributes.value.donorName, namesAttribute, false);
    };

    const handleDonationCausesFromDonationCase = async () => {
      loading.value = true;
      if (
        hasDefaultDonationCauses.value &&
        !hasAnyActiveRequestSessionOrganizationGroupCauses.value
      ) {
        const causeIds: number[] = getDefaultDonationCauseIds();
        if (causeIds.length === 1) {
          donationDetails.value.donationCause = (await getDonationCauseWithId(
            causeIds[0]
          )) as DonationCause;
          donationDetails.value.isFamilySuggested = true; // Mark as family-suggested
          donationDetails.value.familySuggestedCause = donationDetails.value.donationCause; // Store original family suggestion
          trackCharityPreselected(donationDetails.value.donationCause);
          defaultSelectableDonationCauses.value = [donationDetails.value.donationCause!];
          if (
            !defaultSelectableDonationCauses.value ||
            defaultSelectableDonationCauses.value.length === 0
          ) {
            window.log?.error('[Guide] certificate.defaultCause — Failed to fetch default donation cause', { causeIds });
            store.commit('setSnackbar', {
              active: true,
              color: 'error',
              text: 'Failed to fetch default donation cause',
            });
          }
          fetchImages(donationDetails.value.donationCause!.organizationGroup.id);
        } else if (causeIds.length > 1) {
          defaultSelectableDonationCauses.value = await getDonationCausesWithIds(causeIds);
          if (
            !defaultSelectableDonationCauses.value ||
            defaultSelectableDonationCauses.value.length === 0
          ) {
            window.log?.error('[Guide] certificate.defaultCauses — Failed to fetch default donation causes', { causeIds });
            store.commit('setSnackbar', {
              active: true,
              color: 'error',
              text: 'Failed to fetch default donation causes',
            });
          }
        }
      } else if (hasAnyActiveRequestSessionOrganizationGroupCauses.value) {
        // For charities with organization causes, show the organization's causes in the dropdown
        defaultSelectableDonationCauses.value = activeRequestSessionOrganizationGroupCauses.value;
      }
      loading.value = false;
    };

    // getDonationCauseWithId and getDonationCausesWithIds are now provided by useDonationCauses composable

    const handleSearchResults = (results: DonationCause[], query: string) => {
      // When org group restricted, we only need the query for local filtering (filteredDefaultCauses handles it)
      if (!isOrgGroupRestricted.value) {
        searchResults.value = results;
      }
      searchQuery.value = query;
      
      // Reset scroll position to top when search results update
      nextTick(() => {
        const scrollContainer = document.querySelector('.charity-modal-content');
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }
      });
    };

    const handleClearSearch = () => {
      searchQuery.value = '';
      searchResults.value = [];
    };

    const handleClearSelectedCause = () => {
      pendingCause.value = null;
      pendingIsFamilySuggested.value = false;
      searchQuery.value = '';
      searchResults.value = [];
    };

    const selectPreselectedCause = (cause: DonationCause) => {
      pendingCause.value = cause;
      pendingIsFamilySuggested.value = true;
    };

    const openCharityModal = () => {
      pendingCause.value = donationDetails.value.donationCause; // Start the draft from the current charity
      pendingIsFamilySuggested.value = donationDetails.value.isFamilySuggested;
      // Only show loading spinner when the infinite scroll component will emit @ready
      causeModalLoading.value = !isOrgGroupRestricted.value;
      causeModal.value = true;
    };

    const saveCharitySelection = () => {
      const previousId = donationDetails.value.donationCause?.id;
      // charity_saved: only when Save changes the charity to a non-null one
      if (pendingCause.value && pendingCause.value.id !== previousId) {
        matomo.trackEvent('donations_certificate_charity_saved', 'certificate', 'charity_saved');
      }
      donationDetails.value.donationCause = pendingCause.value;
      donationDetails.value.isFamilySuggested = pendingIsFamilySuggested.value;
      causeModal.value = false;
      // organizationGroup is not always populated on a cause (e.g. preselected causes); skip the refetch then
      const orgGroupId = pendingCause.value?.organizationGroup?.id;
      if (orgGroupId && pendingCause.value!.id !== previousId) {
        fetchImages(orgGroupId);
      }
    };

    // Cancel, the X button, tapping outside and Escape all discard the draft
    const cancelCharitySelection = () => {
      matomo.trackEvent('donations_certificate_charity_cancel_selection', 'certificate', 'donations_certificate_charity_cancel_selection');
      causeModal.value = false;
    };

    const onCauseModalToggle = (open: boolean) => {
      if (!open && causeModal.value) cancelCharitySelection();
    };

    const next = async () => {
      // Reset cause error on each attempt
      causeError.value = false;

      // Manual validation for required fields
      let isValid = true;
      let firstInvalidField: string | null = null;
      let missingFields: string[] = [];

      if (isMemoryGift.value) {
        // Recipient name is required for memory gifts
        if (!recipientName.value || !recipientName.value.trim()) {
          isValid = false;
          missingFields.push(t('donationDetails.form.recipientName.label'));
          firstInvalidField = 'recipientname';
        }

        // Donation cause is required for memory gifts
        if (!donationDetails.value.donationCause) {
          isValid = false;
          causeError.value = true;
          missingFields.push(t('donationDetails.form.donationCauseId.label'));
          if (!firstInvalidField) firstInvalidField = 'donation-cause';
        }

        if (!hasValidAttributeRowCount()) {
          isValid = false;
          if (!firstInvalidField) {
            const greeting = getAttributeValueById(AttributeType.GREETING) || '';
            firstInvalidField = certificateMessageFits(greeting, certificateLayoutContext.value)
              ? 'sendername'
              : 'message';
          }
        }

        // Sender name validation commented out - not required
        // Either sender name OR anonymous checkbox must be provided for memory gifts
        // const senderName = getAttributeValueById(attributes.value.donorName)?.trim();
        // if (!senderName && !anonymousSender.value) {
        //   isValid = false;
        //   missingFields.push(t('donationDetails.form.senderName.label') + ' or anonymous');
        //   if (!firstInvalidField) firstInvalidField = 'sendersname';
        // }
      }

      if (!isValid) {
        // Trigger form validation to show errors
        form.value?.validate();
        // Scroll to the charity section if that's the issue, otherwise scroll to the first invalid field
        if (causeError.value) {
          nextTick(() => causeSectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
        } else if (firstInvalidField) {
          const element = document.querySelector(`[data-cy*="${firstInvalidField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return;
      }

      // Also try form validation as backup
      const { valid: formValid } = await form.value.validate();
      if (!formValid) {
        // Find first invalid control and scroll to it (avoid $children so code works in Vue 3)
        try {
          // Vuetify 3 no longer exposes $vuetify on the instance proxy; use null and let helper fallback
          scrollToFirstInvalid(form.value, null);
        } catch {
          // no-op
        }
        return;
      }

      matomo.trackEvent('donations_certificate_details_next_clicked', 'certificate', 'donations_certificate_next_clicked');

      const donationVal: Donation = donation.value;
      if (isMemoryGift.value) {
        cleanAttributeTexts();
        donationVal.attributeValues = donationDetails.value
          .attributeValues as Array<AttributeValue>;
        const usingUploadedPhoto = !donationDetails.value.skipTheme && !!uploadedImageKey.value;
        donationVal.donationImageId = donationDetails.value.skipTheme || usingUploadedPhoto
          ? blankImageId.value
          : selectedImageId.value || blankImageId.value;
        donationVal.customCertificateImageUrl = donationDetails.value.skipTheme
          ? null
          : uploadedImageUrl.value || null;
        donationVal.imageKey = usingUploadedPhoto ? uploadedImageKey.value : null;
      }

      donationVal.donationCause = donationDetails.value.donationCause as DonationCause | null;
      donationVal.showCondolenceOnMemorialPage =
        !!donationDetails.value.showCondolenceOnMemorialPage;
      store.commit('updateDonation', donationVal);
      emit('nextStep');
    };

    watch(anonymousSender, (newVal: boolean) => {
      if (newVal) {
        // Clear the sender name when anonymous is checked
        setAttributeValueById(attributes.value.donorName, '', false);
      }
      nextTick(() => {
        form.value?.validate();
      });
    });

    watch(donorNameValue, () => {
      nextTick(() => form.value?.validate());
    });

    watch(donation, (next) => {
      hasBeenPrefilled.value = false;
      anonymousSender.value = false;
      causeError.value = false;
      selectedImageId.value = images.value.length > 0 ? images.value[0].id : 0;
      if (next?.imageKey) {
        uploadedImageKey.value = next.imageKey;
        if (next.customCertificateImageUrl) {
          uploadedImageUrl.value = next.customCertificateImageUrl;
        }
      } else {
        uploadedImageUrl.value = '';
        uploadedImageKey.value = '';
      }
      donationDetails.value = {
        showCondolenceOnMemorialPage: false,
        attributeValues: [
          { value: '', attributeId: AttributeType.SUBJECT_NAME },
          { value: '', attributeId: AttributeType.GREETING },
          { value: '', attributeId: AttributeType.DONOR_NAME },
        ] as Array<AttributeValue>,
        donationCause: null,
        donationImageId: '',
        skipTheme: false,
        isFamilySuggested: false,
        familySuggestedCause: null,
      };
    });

    watch(currentStep, (newVal, oldVal) => {
      if (newVal === stepV2.CERTIFICATE_DETAILS && oldVal < stepV2.CERTIFICATE_DETAILS) {
        calculateShowCause();
        handleDonationCausesFromDonationCase();
        prefillRecipientNameFromCase();
        matomo.trackEvent('donations_certificate_viewed', 'certificate', 'donations_certificate_viewed');
      }
    });

    watch(
      () => donation.value?.donationCase?.caseInfo?.name,
      (newName, oldName) => {
        if (newName && newName !== oldName) {
          hasBeenPrefilled.value = false;
          recipientName.value = '';
          prefillRecipientNameFromCase();
        }
      }
    );

    watch(
      () => donationDetails.value.skipTheme,
      (val) => {
        if (val) {
          donationDetails.value.donationImageId = null;
        } else if (!donationDetails.value.donationImageId && selectedImageId.value) {
          donationDetails.value.donationImageId = selectedImageId.value;
        }
      }
    );

    // Clear the charity error as soon as the user picks one
    watch(
      () => donationDetails.value.donationCause,
      (newVal) => {
        if (newVal) causeError.value = false;
      }
    );

    watch(
      greetingValue,
      () => {
        calculateShowCause();
        showCondolenceOptions.value = !!(
          requestSession.value &&
          'showCondolenceOnMemorialPage' in requestSession.value &&
          requestSession.value.showCondolenceOnMemorialPage
        );
      },
      { immediate: true }
    );

    // There is nothing to publish without a greeting or a sender name. On as soon as there
    // is something to show, off again once both are empty. In between the user is
    // free to toggle it off and still get the certificate printed.
    watch(
      hasCondolenceContent,
      (hasContent) => {
        donationDetails.value.showCondolenceOnMemorialPage = hasContent;
      },
      { immediate: true }
    );

    watch(selectedImageId, (value) => {
      if (value) {
        donationDetails.value.donationImageId = value;
      }
    });

    onMounted(async () => {
      // immediately fetch image by categories if the request session has an organization group with active causes, OR there is no attached organization group
      if (hasAnyActiveRequestSessionOrganizationGroupCauses.value) {
        await fetchImages(requestSession.value?.organizationGroup?.id);
      } else if (!requestSession.value?.organizationGroup) {
        await fetchImages();
      }
      
      // Set the default selected image ID AFTER fetching images completes
      const existingCustomUrl = donation.value?.customCertificateImageUrl;
      const existingImageKey = donation.value?.imageKey;
      const existingImageId = donation.value?.donationImageId;
      const restorePreviousCrop = !donation.value?.paymentSpecification?.paid;
      if (restorePreviousCrop && existingCustomUrl) {
        uploadedImageUrl.value = existingCustomUrl;
      }
      if (restorePreviousCrop && existingImageKey) {
        uploadedImageKey.value = existingImageKey;
      }
      if (existingImageId && images.value.some((img) => img.id === existingImageId)) {
        selectedImageId.value = existingImageId;
      } else if (images.value.length > 0) {
        selectedImageId.value = images.value[0].id;
      }
      
      if (currentStep.value === stepV2.CERTIFICATE_DETAILS) {
        calculateShowCause();
        handleDonationCausesFromDonationCase();
      }
      prefillRecipientNameFromCase();
    });

    return {
      form,
      hasBeenPrefilled,
      showCertificatePreview,
      anonymousSender,
      customiseGiftCard,
      isChecked,
      showSuggestions,
      appendInsteadOfReplace,
      showImagePickerModal,
      showUploadImageModal,
      showTermsOfUse,
      uploadedImageUrl,
      uploadedImageKey,
      certificatePhotoAspect: CERTIFICATE_PHOTO_ASPECT,
      valid,
      showCondolenceOptions,
      condolenceToggleDisabled,
      showCause,
      causeError,
      causeErrorMessage,
      causeSectionRef,
      selectedImageId,
      blankImageId,
      selectedImage, 
      images,
      categories,
      loading,
      searchQuery,
      searchResults,
      defaultSelectableDonationCauses,
      defaultSelectableDonationCausesAny,
      donationDetailsDonationCauseAny,
      pendingCause,
      onCauseModalToggle,
      donationDetailsImageIdAny,
      addTheme,
      donationCauseIdAny,
      defaultImage,
      textAreaValidation,
      exampleMessages,
      attributes,
      donationDetails,
      donorNameValue,
      senderOrAnonymousRules,
      recipientName,
      defaultRecipientName,
      showRecipientNameReset,
      resetRecipientName,
      isIphone,
      currentStep,
      greetingValue,
      giftCardDeceasedName,
      canPreviewCertificate,
      nonEmptyCategories,
      requestSession,
      donation,
      isMemoryGift,
      isContribution,
      tooManyAttributeRows,
      hasDefaultDonationCauses,
      activeRequestSessionOrganizationGroupCauses,
      hasAnyActiveRequestSessionOrganizationGroupCauses,
      activeDonationCauses,
      hasSelectableCausesToDisplay,
      hasOrganizationGroupCausesToDisplay,
      isExternalAgent,
      isOnCertificateDetailsPage,
      showPreviousButton,
      isSmallScreen,
      rules,
      t,
      onPreselectedDonationCauseChanged,
      onPreselectedDonationCauseCleared,
      getDefaultDonationCauseIds,
      prefillRecipientNameFromCase,
      getAttributeValueById,
      setAttributeValueById,
      onRecipientNameFocus,
      onSenderNameFocus,
      onImageSelected,
      onImageUploaded,
      hasValidAttributeRowCount,
      tooManyRowsRule,
      selectMessage,
      fetchImages,
      calculateShowCause,
      cleanAttributeTexts,
      handleDonationCausesFromDonationCase,
      getDonationCauseWithId,
      getDonationCausesWithIds,
      handleSearchResults,
      handleClearSearch,
      handleClearSelectedCause,
      selectPreselectedCause,
      openCharityModal,
      saveCharitySelection,
      cancelCharitySelection,
      next,
      causeModal,
      isForcedSingleCause,
      causeModalLoading,
      isOrgGroupRestricted,
      filteredDefaultCauses,
    };
  },
});
</script>
<style scoped>
/* "Reset to ..." link under the recipient name field */
.recipient-name-reset {
  margin: 0 0 22px 0px;
  font-size: 12px;
  font-weight: 700;
  color: #047481;
  text-decoration: underline;
  cursor: pointer;
}
.recipient-name-reset:hover {
  color: #035c66;
}

/* Helper text under the recipient name field */
.recipient-name-helper {
  margin: -10px 0 25px 15px;
  font-size: 12px;
  color: #6b7280;
}

/* "Need inspiration?" link + greeting suggestions panel */
.greeting-suggestions-wrapper {
  margin-bottom: 16px;
}

.need-inspiration-link {
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #047481;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
}

/* Underline only the text, not the icon */
.need-inspiration-link :deep(.v-icon) {
  text-decoration: none;
}

.need-inspiration-link .inspiration-text {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.greeting-suggestions-panel {
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  background-color: #FFFFFF;
  overflow: hidden;
}

.greeting-suggestions-title {
  font-weight: 700;
  font-size: 16px;
  color: #1F2A37;
}

/* Strip the default border/background that wraps the checkbox control */
.append-checkbox :deep(.v-input__control),
.append-checkbox :deep(.v-selection-control),
.append-checkbox :deep(.v-field) {
  border: none !important;
  background-color: transparent !important;
}

.append-checkbox :deep(.v-label) {
  font-size: 14px !important;
  opacity: 1;
  color: #374151;
}

.greeting-suggestions-list {
  max-height: 320px;
  overflow-y: auto;
}

.greeting-suggestion-item {
  padding: 14px 16px;
  font-size: 15px;
  color: #374151;
  cursor: pointer;
  border-bottom: 1px solid #F0F2F4;
}

.greeting-suggestion-item:last-child {
  border-bottom: none;
}

.greeting-suggestion-item:hover {
  background-color: #F5F7F8;
}

/* "Customise gift card" toggle header above the dashed container */
.customise-gift-card-header__title {
  font-family: 'Lato', sans-serif;
  font-weight: 800;
  font-size: 16px;
  color: #6B7280;
}

.customise-gift-card-header__optional {
  font-weight: 400;
  color: #6b7280;
}

/* Keep this toggle's row transparent (it sits on the white header, not the tinted card) */
.customise-gift-card-toggle.skip-theme-toggle,
.customise-gift-card-toggle.skip-theme-toggle :deep(.v-input__control),
.customise-gift-card-toggle.skip-theme-toggle :deep(.v-selection-control) {
  background-color: transparent !important;
}

/* Strip the default v-switch min-height so it stays vertically centered with the
   header title, but leave the thumb's own insets intact so it doesn't touch the edges */
.customise-gift-card-toggle.skip-theme-toggle :deep(.v-input__control),
.customise-gift-card-toggle.skip-theme-toggle :deep(.v-selection-control) {
  min-height: 0 !important;
}

.gift-card-container {
  border: 1px dashed #047481;
  background-color: #F5F8F7;
  border-radius: 16px !important;
  margin-bottom: 20px;
}

.gift-card-info {
  background-color: #E6F5F5;
  padding: 20px 24px;
  border-radius: 6px;
}

.gift-card-info__icon {
  color: #047481 !important;
  width: 18px !important;
  height: 16px !important;
}

.gift-card-info__title {
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: #03543F;
}

.gift-card-info__text {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #03543F;
  margin: 0;
}

/* "Sender information" box (shown when gift card customisation is off) */
.sender-info {
  background-color: #FFFFFF;
  padding: 20px 24px;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);
  margin-bottom: 24px;
}

.sender-info__icon {
  color: #6B7280 !important;
  width: 18px !important;
  height: 18px !important;
}

.sender-info__title {
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: #6B7280;
}

.sender-info__text {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #6B7280;
  margin: 0;
}

.certificate-image-placeholder {
  width: 100%;
  height: auto;
  border-radius: 8px;
  object-fit: cover;
  object-position: center;
  display: block;
}

.image-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 37px !important;
  border: 1px solid #D1D5DB;
  border-radius: 8px !important;
  box-shadow: none !important;
  text-transform: uppercase !important;
  font-weight: 800 !important;
  font-size: 14px !important;
  color: #047481 !important;
  letter-spacing: normal !important;
  text-decoration: none !important;
  min-width: unset !important;
  width: 100% !important;
  font-family: 'Lato', sans-serif !important;
  
  @media (max-width: 600px) {
    font-size: 14px !important;
  }
}

.image-action-btn :deep(.v-btn__content) {
  text-decoration: none !important;
  font-weight: 800 !important;
}

/* Remove border from the skip theme toggle (and condolence toggle) - using deep selector to penetrate Vuetify's component scope */
:deep(.skip-theme-toggle) .v-input__control,
:deep(.no-sender-name.skip-theme-toggle) .v-input__control {
  border: none !important;
}

/* Tint the skip-theme toggle row so it matches the surrounding #F5F7F8 background */
.skip-theme-toggle,
.skip-theme-toggle :deep(.v-input__control),
.skip-theme-toggle :deep(.v-selection-control) {
  background-color: #F5F7F8 !important;
}

/* Reduce the toggle label text size */
:deep(.skip-theme-toggle) .v-label,
:deep(.no-sender-name.skip-theme-toggle) .v-label {
  font-size: 14px !important;
  color: #374151;
  opacity: 1 !important;
  white-space: normal !important;
}

/* Condolence toggle disabled state (no greeting text) */
:deep(.no-sender-name.skip-theme-toggle.v-input--disabled) .v-switch__track {
  background-color: #D1D5DB !important;
}
:deep(.no-sender-name.skip-theme-toggle.v-input--disabled) .v-label {
  color: #D1D5DB !important;
  opacity: 1 !important;
}
:deep(.no-sender-name.skip-theme-toggle.v-input--disabled) .v-switch__thumb {
  background-color: white !important;
}


/* Make the switch track thicker - gray when not selected */
:deep(.skip-theme-toggle) .v-switch__track,
:deep(.no-sender-name.skip-theme-toggle) .v-switch__track {
  height: 20px !important;
  width: 40px !important;
  opacity: 1 !important;
  background-color: #A8A8A8;
}

:deep(.skip-theme-toggle) .v-switch__thumb,
:deep(.no-sender-name.skip-theme-toggle) .v-switch__thumb {
  height: 16px !important;
  width: 16px !important;
  background-color: white !important;
}

/* Override bg-primary class to keep thumb white */
:deep(.skip-theme-toggle) .v-switch__thumb.bg-primary,
:deep(.no-sender-name.skip-theme-toggle) .v-switch__thumb.bg-primary {
  background-color: white !important;
}

.donation-cause-display {
  display: flex;
  align-items: center;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: white;
  cursor: pointer;
  margin-top: 8px;
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    border-radius: 16px;
  }
}

.donation-cause-content {
  @media (max-width: 600px) {
    margin-bottom: 12px;
  }

  .font-weight-bold {
    font-family: 'Lato', sans-serif;
  }
}

.donation-cause-display:hover {
  border-color: #d1d5db;
}

.change-btn {
  text-transform: uppercase;
  font-weight: bold;
  
  @media (max-width: 600px) {
    align-self: center;
  }
}

.text-grey {
  color: #6b7280;
  font-size: 14px;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.charity-modal {
  max-height: 90vh;
  max-width: 100%;

  @media (max-width: 450px) {
    height: 100vh;
    max-height: 100vh;
    margin: 0 !important;
    border-radius: 0 !important;
  }
}

:deep(.v-dialog) {
  @media (max-width: 450px) {
    margin: 0 !important;
  }
}

:deep(.v-overlay__content) {
  @media (max-width: 450px) {
    margin: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    bottom: 0 !important;
    position: fixed !important;
  }
}

.charity-modal-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  
  @media (max-width: 600px) {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
  }
}

.charity-modal-content :deep(.v-card-text) {
  margin-top: 10px;
  overflow: visible;
}

.charity-modal-content :deep(.donation-cause-infinite-scroll) {
  max-height: none !important;
  overflow: visible !important;
}

.charity-modal-actions {
  padding: 24px !important;
  border-top: 1px solid #e5e7eb;
  background: white;
}

.charity-modal-actions .v-btn {
  flex: 1;
  border-radius: 8px;
  max-height: 34px; 
  font-size: 12px;
  font-weight: 700 !important;
  font-family: "Inter", sans-serif;
  letter-spacing: .5px;
}

.preselected-charity-item {
  background: white;
  cursor: pointer;
}

.donation-flow__option-title {
    font-family: "Lato", sans-serif;
    font-weight: 700;
    color: #6B7280;
    height: 16px;
  }

.certificate-details-actions {
  width: 100%;
}

.certificate-terms {
  margin: 12px 0 0;
  padding: 0;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: #6B7280;
  text-align: right;
}

.certificate-terms__link {
  padding: 0;
  border: 0;
  background: none;
  color: #047481;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.certificate-terms-dialog__title {
  font-family: 'Lato', sans-serif;
  font-size: 18px;
  font-weight: 700;
}

.certificate-terms-dialog__body {
  max-height: 70vh;
}

@media (max-width: 600px) {
  .certificate-terms {
    text-align: center;
    margin: 12px 0 0;
  }
}

</style>
